/**
 * Referential-integrity-on-delete: the shared, collection-agnostic scan behind the `lTid`
 * dangling-reference repair (#4442). Extracted so any collection with an id-list field that points at
 * deletable rows (e.g. `BudgetStoreModel.ctId`) reuses this mechanism instead of hand-rolling the
 * loop a second time. See #4705.
 */

/**
 * Keys of `T` whose value is a `string[]` — the only fields a dangling-id repair can operate on.
 * Constrains callers to real id-list fields (e.g. `lTid`/`xLid` on a transaction, `ctId` on a
 * budget), so a string / number / object field can't be passed in by mistake.
 */
export type IdListKey<T> = {
  [K in keyof T]-?: T[K] extends string[] ? K : never;
}[keyof T];

/**
 * Build repaired copies of every model — except the ones being deleted — that hold a dangling
 * reference to a deleted id in the array-of-ids `field`, with those ids stripped. Models with no
 * dangling reference are omitted (nothing to write). Pure: the inputs are never mutated (each repaired
 * model is a fresh shallow copy with a filtered `field`).
 */
export function repairDanglingReferences<T extends { id: string }>(
  models: Iterable<T>,
  field: IdListKey<T>,
  deletedIds: ReadonlySet<string>,
): T[] {
  const repaired: T[] = [];
  for (const model of models) {
    if (deletedIds.has(model.id)) {
      continue;
    }
    // Tolerate a missing/undefined OR non-array list: a legacy row can omit a field added in a later
    // store version (e.g. `xLid`, added in v8 — see the transaction model DTO), and corrupt data can
    // hold a non-array value in a field typed `string[]` (e.g. a budget saved with `ctId: "cat-1"`).
    // Delete scans EVERY stored model before it writes, so a throw here would abort the whole delete,
    // unlinked rows and all — one malformed row would block every delete. `?.` alone guards only
    // null/undefined (an unguarded `.some` on a bare string throws), so gate on Array.isArray; such a
    // row simply has nothing to repair. Fixing the bad data at load is tracked separately (#4313).
    const refs = model[field] as string[] | undefined;
    if (Array.isArray(refs) && refs.some((id) => deletedIds.has(id))) {
      repaired.push({
        ...model,
        [field]: refs.filter((id) => !deletedIds.has(id)),
      } as T);
    }
  }
  return repaired;
}
